/**
 * Helpers for managing the Office Add-in session token.
 *
 * WebView2 (the browser control Office uses for task-pane add-ins) silently
 * drops `secure: true` cookies on localhost.  As a workaround, after the
 * MSAL-to-Better-Auth token exchange we persist the session token in
 * `sessionStorage` and inject it as an `Authorization: Bearer` header on
 * every outgoing fetch request while inside the add-in context.
 *
 * The server-side `getSession()` in `lib/auth.ts` checks for Bearer tokens
 * first and resolves the session directly from the database, bypassing
 * better-auth's `nextCookies()` plugin (which only reads from the real
 * cookie jar and cannot see manually-injected headers).
 *
 * "Inside the add-in" is determined by the host marker (cookie +
 * sessionStorage), set by the manifest-driven bootstrap page. Path-based
 * detection won't work — once the user navigates from `/office-addin/taskpane`
 * to `/chat/abc` or `/workspaces`, the URL no longer carries the marker.
 */

import { ADDIN_SESSION_COOKIE_NAME } from "./embedded-hosts";
import { getClientHost, isOfficeAddin } from "./host";

const STORAGE_KEY = "magnus-office-addin-session-token";

/** Returns `true` when the page is running inside any Office Add-in host. */
export function isOfficeAddinContext(): boolean {
  return isOfficeAddin(getClientHost());
}

/**
 * Mirror the session token into `document.cookie`.
 *
 * Why we need this even though the exchange route already sets the
 * cookie via `Set-Cookie`:
 *
 *   - WebView2 (the control Office uses for task panes) intermittently
 *     drops the `httpOnly` `Set-Cookie` from the exchange response on
 *     localhost+self-signed-cert dev setups.
 *   - When the cookie is missing, every server-side `getSession()` call
 *     (chat page, agents page, workspaces, admin, layout, the
 *     `getServerCaller()` machinery) returns null and protected pages
 *     either render empty or throw `UNAUTHORIZED`.
 *   - Writing the same token via `document.cookie` reliably puts it in
 *     the cookie jar — WebView2 keeps cookies set from JS just fine
 *     (the `magnus-host` cookie set by `/api/host/mark` is the proof).
 *
 * Trade-off: this cookie is necessarily non-`httpOnly` (JS sets it),
 * but the same token already lives in `sessionStorage` for the Bearer
 * header path, so cookie-jar visibility from JS is not a new exposure.
 *
 * The exchange's server-set cookie is still preferred when WebView2
 * keeps it (it's `httpOnly` and authoritative). The browser merges
 * same-name cookies by last write, so this just ensures the value is
 * present when the server-set version was dropped.
 */
function writeSessionCookie(token: string): void {
  if (typeof document === "undefined") {
    return;
  }
  // Dev-only fallback. Production WebView2 keeps the `httpOnly`
  // `Set-Cookie` from `/api/auth/app-exchange` reliably (real
  // HTTPS, trusted cert). Skipping the JS write in production preserves
  // `httpOnly` on the authoritative cookie.
  if (process.env.NODE_ENV === "production") {
    return;
  }
  const maxAgeSeconds = 30 * 24 * 60 * 60;
  // No `Secure` so WebView2 keeps it on localhost without forcing HTTPS
  // attribute checks. `SameSite=Lax` matches what the exchange route sets in
  // dev — production (where it uses `None; Partitioned`) returns above.
  document.cookie = `${ADDIN_SESSION_COOKIE_NAME}=${token}; path=/; max-age=${maxAgeSeconds}; samesite=lax`;
}

function clearSessionCookie(): void {
  if (typeof document === "undefined") {
    return;
  }
  if (process.env.NODE_ENV === "production") {
    return;
  }
  document.cookie = `${ADDIN_SESSION_COOKIE_NAME}=; path=/; max-age=0; samesite=lax`;
}

/**
 * Persist the session token received from the app-exchange
 * endpoint. Writes to BOTH `sessionStorage` (for the Bearer header
 * path) AND `document.cookie` (for server-side RSC + tRPC callers).
 */
export function setOfficeAddinSessionToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
    console.info("[Magnus Add-in] Session token saved to sessionStorage");
  } catch {
    console.warn(
      "[Magnus Add-in] Failed to write session token to sessionStorage"
    );
  }
  writeSessionCookie(token);
}

/** Read the stored session token (returns `null` when absent). */
export function getOfficeAddinSessionToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Remove the stored session token (e.g. on sign-out). Clears both
 * `sessionStorage` and the cookie-jar copy.
 */
export function clearOfficeAddinSessionToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  clearSessionCookie();
}

/**
 * Returns extra headers that should be merged into every outgoing fetch
 * request when running inside the Office Add-in.
 *
 * We use a standard `Authorization: Bearer <token>` header instead of a
 * manual `Cookie` header because better-auth's `nextCookies()` plugin reads
 * cookies via Next.js's `cookies()` API (the real cookie jar), **not** from
 * raw request headers.  A manually-injected `Cookie` header is invisible to
 * that plugin.  The server-side `getSession()` helper in `lib/auth.ts`
 * checks for Bearer tokens first and resolves the session directly from the
 * database, bypassing `nextCookies()` entirely.
 */
export function getOfficeAddinSessionHeaders(): Record<string, string> {
  if (!isOfficeAddinContext()) {
    return {};
  }

  const token = getOfficeAddinSessionToken();
  if (!token) {
    return {};
  }

  return { Authorization: `Bearer ${token}` };
}
